import { useEffect, useRef, useCallback } from 'react';

/**
 * useRealtime — React hook that opens a WebSocket to /api/ws/notifications
 * and dispatches incoming events to a handler. Auto-reconnects with
 * exponential backoff. Uses the user's JWT for auth.
 *
 * Usage:
 *   useRealtime(({ type, ...event }) => {
 *     if (type === 'task_assigned' || type === 'task_auto_promoted') refresh();
 *     if (type === 'submission_approved') showToast('Approved!');
 *   });
 *
 * The hook reads REACT_APP_BACKEND_URL and the auth token from localStorage
 * (matches AuthContext convention). If no token, the hook is a no-op.
 */
export default function useRealtime(handler) {
  const handlerRef = useRef(handler);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const backoffRef = useRef(1000);   // 1s, doubles up to 30s

  // Keep the latest handler in a ref so reconnect doesn't churn.
  useEffect(() => { handlerRef.current = handler; }, [handler]);

  const connect = useCallback(() => {
    const token = localStorage.getItem('vos_token');
    if (!token) return;

    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    if (!apiUrl) return;
    // Convert https://host → wss://host (or http://host → ws://host)
    const wsUrl = apiUrl.replace(/^http/, 'ws') + `/api/ws/notifications?token=${encodeURIComponent(token)}`;

    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[realtime] connect failed:', e);
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      // Reset backoff on successful connect
      backoffRef.current = 1000;
      // Light keepalive — server echoes 'pong' if we send 'ping'.
      // Only useful behind aggressive proxies. Browser handles ping frames natively.
    };

    ws.onmessage = (msg) => {
      // 'pong' replies are bare strings; events are JSON.
      if (msg.data === 'pong') return;
      let data;
      try { data = JSON.parse(msg.data); } catch { return; }
      if (handlerRef.current) {
        try { handlerRef.current(data); } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[realtime] handler threw:', e);
        }
      }
    };

    ws.onclose = (e) => {
      // 4401 = unauthorized (custom). Don't reconnect — token is bad.
      if (e.code === 4401) return;
      scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires after onerror — let close handler do the scheduling.
    };
  }, []);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimer.current) return;
    const delay = Math.min(backoffRef.current, 30000);
    reconnectTimer.current = setTimeout(() => {
      reconnectTimer.current = null;
      backoffRef.current = Math.min(backoffRef.current * 2, 30000);
      connect();
    }, delay);
  }, [connect]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = null;
      }
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [connect]);

  // Expose nothing — the handler covers all event consumption.
}
